import { ApiResponse, baseApi } from './api';
import type {
  ScheduleTask,
  ScheduleBaseline,
  ScheduleSettings,
  ScheduleStats,
  LookAheadReport,
  GetScheduleTasksParams,
  CreateScheduleTaskRequest,
  UpdateScheduleTaskRequest,
  UpdateProgressRequest,
  CreateBaselineRequest,
  UpdateSettingsRequest,
  BulkReorderRequest,
  AddDependencyRequest,
  ImportOptions,
  ExportOptions,
} from '../types/schedule';

// Schedule API endpoints
export const scheduleApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ========================================
    // SCHEDULE TASK ENDPOINTS
    // ========================================

    // Get all tasks for a project
    getScheduleTasks: builder.query<
      ApiResponse<{
        tasks: ScheduleTask[];
        summary: ScheduleStats;
        pagination: { page: number; limit: number; total: number; totalPages: number };
      }>,
      { projectId: string; params?: GetScheduleTasksParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/schedule`,
        params,
      }),
      providesTags: (result, error, { projectId }) =>
        result?.data?.tasks
          ? [
              ...result.data.tasks.map(({ _id }) => ({ type: 'Schedule' as const, id: _id })),
              { type: 'Schedule', id: `LIST_${projectId}` },
            ]
          : [{ type: 'Schedule', id: `LIST_${projectId}` }],
    }),

    // Get a single task
    getScheduleTask: builder.query<
      ApiResponse<ScheduleTask>,
      { projectId: string; taskId: string }
    >({
      query: ({ projectId, taskId }) => `/projects/${projectId}/schedule/${taskId}`,
      providesTags: (result, error, { taskId }) => [{ type: 'Schedule', id: taskId }],
    }),

    // Create a new task
    createScheduleTask: builder.mutation<
      ApiResponse<ScheduleTask>,
      { projectId: string; data: CreateScheduleTaskRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/schedule`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Schedule', id: `LIST_${projectId}` },
        { type: 'Schedule', id: `STATS_${projectId}` },
      ],
    }),

    // Update a task
    updateScheduleTask: builder.mutation<
      ApiResponse<ScheduleTask>,
      { projectId: string; taskId: string; data: UpdateScheduleTaskRequest }
    >({
      query: ({ projectId, taskId, data }) => ({
        url: `/projects/${projectId}/schedule/${taskId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId, taskId }) => [
        { type: 'Schedule', id: taskId },
        { type: 'Schedule', id: `LIST_${projectId}` },
        { type: 'Schedule', id: `STATS_${projectId}` },
      ],
    }),

    // Delete a task
    deleteScheduleTask: builder.mutation<ApiResponse<void>, { projectId: string; taskId: string }>({
      query: ({ projectId, taskId }) => ({
        url: `/projects/${projectId}/schedule/${taskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { projectId, taskId }) => [
        { type: 'Schedule', id: taskId },
        { type: 'Schedule', id: `LIST_${projectId}` },
        { type: 'Schedule', id: `STATS_${projectId}` },
      ],
    }),

    // Update task progress
    updateScheduleTaskProgress: builder.mutation<
      ApiResponse<ScheduleTask>,
      { projectId: string; taskId: string; data: UpdateProgressRequest }
    >({
      query: ({ projectId, taskId, data }) => ({
        url: `/projects/${projectId}/schedule/${taskId}/progress`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId, taskId }) => [
        { type: 'Schedule', id: taskId },
        { type: 'Schedule', id: `LIST_${projectId}` },
        { type: 'Schedule', id: `STATS_${projectId}` },
      ],
    }),

    // Bulk reorder tasks
    bulkReorderScheduleTasks: builder.mutation<
      ApiResponse<void>,
      { projectId: string; data: BulkReorderRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/schedule/reorder`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Schedule', id: `LIST_${projectId}` },
      ],
    }),

    // ========================================
    // DEPENDENCY ENDPOINTS
    // ========================================

    // Add a dependency
    addScheduleDependency: builder.mutation<
      ApiResponse<ScheduleTask>,
      { projectId: string; taskId: string; data: AddDependencyRequest }
    >({
      query: ({ projectId, taskId, data }) => ({
        url: `/projects/${projectId}/schedule/${taskId}/dependencies`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId, taskId }) => [
        { type: 'Schedule', id: taskId },
        { type: 'Schedule', id: `LIST_${projectId}` },
      ],
    }),

    // Remove a dependency
    removeScheduleDependency: builder.mutation<
      ApiResponse<ScheduleTask>,
      { projectId: string; taskId: string; predecessorId: string }
    >({
      query: ({ projectId, taskId, predecessorId }) => ({
        url: `/projects/${projectId}/schedule/${taskId}/dependencies/${predecessorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { projectId, taskId }) => [
        { type: 'Schedule', id: taskId },
        { type: 'Schedule', id: `LIST_${projectId}` },
      ],
    }),

    // ========================================
    // STATS & ANALYSIS ENDPOINTS
    // ========================================

    // Get schedule statistics
    getScheduleStats: builder.query<ApiResponse<ScheduleStats>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/schedule/stats`,
      providesTags: (result, error, { projectId }) => [
        { type: 'Schedule', id: `STATS_${projectId}` },
      ],
    }),

    // Get critical path
    getCriticalPath: builder.query<ApiResponse<ScheduleTask[]>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/schedule/critical-path`,
      providesTags: (result, error, { projectId }) => [
        { type: 'Schedule', id: `CRITICAL_PATH_${projectId}` },
      ],
    }),

    // Get look-ahead report
    getLookAhead: builder.query<
      ApiResponse<LookAheadReport>,
      { projectId: string; weeks?: number; includeCompleted?: boolean }
    >({
      query: ({ projectId, weeks, includeCompleted }) => ({
        url: `/projects/${projectId}/schedule/look-ahead`,
        params: { weeks, includeCompleted },
      }),
      providesTags: (result, error, { projectId }) => [
        { type: 'Schedule', id: `LOOK_AHEAD_${projectId}` },
      ],
    }),

    // ========================================
    // BASELINE ENDPOINTS
    // ========================================

    // Get all baselines
    getScheduleBaselines: builder.query<ApiResponse<ScheduleBaseline[]>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/schedule/baselines`,
      providesTags: (result, error, { projectId }) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: 'ScheduleBaseline' as const,
                id: _id,
              })),
              { type: 'ScheduleBaseline', id: `LIST_${projectId}` },
            ]
          : [{ type: 'ScheduleBaseline', id: `LIST_${projectId}` }],
    }),

    // Get a single baseline
    getScheduleBaseline: builder.query<
      ApiResponse<ScheduleBaseline>,
      { projectId: string; baselineId: string }
    >({
      query: ({ projectId, baselineId }) =>
        `/projects/${projectId}/schedule/baselines/${baselineId}`,
      providesTags: (result, error, { baselineId }) => [
        { type: 'ScheduleBaseline', id: baselineId },
      ],
    }),

    // Create a baseline
    createScheduleBaseline: builder.mutation<
      ApiResponse<ScheduleBaseline>,
      { projectId: string; data: CreateBaselineRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/schedule/baselines`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'ScheduleBaseline', id: `LIST_${projectId}` },
      ],
    }),

    // Delete a baseline
    deleteScheduleBaseline: builder.mutation<
      ApiResponse<void>,
      { projectId: string; baselineId: string }
    >({
      query: ({ projectId, baselineId }) => ({
        url: `/projects/${projectId}/schedule/baselines/${baselineId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { projectId, baselineId }) => [
        { type: 'ScheduleBaseline', id: baselineId },
        { type: 'ScheduleBaseline', id: `LIST_${projectId}` },
      ],
    }),

    // ========================================
    // SETTINGS ENDPOINTS
    // ========================================

    // Get schedule settings
    getScheduleSettings: builder.query<ApiResponse<ScheduleSettings>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/schedule/settings`,
      providesTags: (result, error, { projectId }) => [{ type: 'ScheduleSettings', id: projectId }],
    }),

    // Update schedule settings
    updateScheduleSettings: builder.mutation<
      ApiResponse<ScheduleSettings>,
      { projectId: string; data: UpdateSettingsRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/schedule/settings`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'ScheduleSettings', id: projectId },
      ],
    }),

    // ========================================
    // IMPORT/EXPORT ENDPOINTS
    // ========================================

    // Get import template
    getImportTemplate: builder.query<Blob, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/schedule/import/template`,
        responseHandler: response => response.blob(),
      }),
    }),

    // Import tasks from CSV
    importScheduleTasks: builder.mutation<
      ApiResponse<{ imported: number; updated: number; errors: string[] }>,
      { projectId: string; file: File; options?: ImportOptions }
    >({
      query: ({ projectId, file, options }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (options) {
          Object.entries(options).forEach(([key, value]) => {
            formData.append(key, String(value));
          });
        }
        return {
          url: `/projects/${projectId}/schedule/import`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Schedule', id: `LIST_${projectId}` },
        { type: 'Schedule', id: `STATS_${projectId}` },
      ],
    }),

    // Export tasks to CSV
    exportScheduleTasks: builder.query<Blob, { projectId: string; options?: ExportOptions }>({
      query: ({ projectId, options }) => ({
        url: `/projects/${projectId}/schedule/export`,
        params: options,
        responseHandler: response => response.blob(),
      }),
    }),

    // Export summary report
    exportScheduleSummary: builder.query<Blob, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/schedule/export/summary`,
        responseHandler: response => response.blob(),
      }),
    }),

    // Export look-ahead report
    exportLookAhead: builder.query<Blob, { projectId: string; weeks?: number }>({
      query: ({ projectId, weeks }) => ({
        url: `/projects/${projectId}/schedule/export/look-ahead`,
        params: { weeks },
        responseHandler: response => response.blob(),
      }),
    }),
  }),
  overrideExisting: false,
});

// Export hooks for use in components
export const {
  // Task hooks
  useGetScheduleTasksQuery,
  useLazyGetScheduleTasksQuery,
  useGetScheduleTaskQuery,
  useLazyGetScheduleTaskQuery,
  useCreateScheduleTaskMutation,
  useUpdateScheduleTaskMutation,
  useDeleteScheduleTaskMutation,
  useUpdateScheduleTaskProgressMutation,
  useBulkReorderScheduleTasksMutation,

  // Dependency hooks
  useAddScheduleDependencyMutation,
  useRemoveScheduleDependencyMutation,

  // Stats & analysis hooks
  useGetScheduleStatsQuery,
  useLazyGetScheduleStatsQuery,
  useGetCriticalPathQuery,
  useLazyGetCriticalPathQuery,
  useGetLookAheadQuery,
  useLazyGetLookAheadQuery,

  // Baseline hooks
  useGetScheduleBaselinesQuery,
  useLazyGetScheduleBaselinesQuery,
  useGetScheduleBaselineQuery,
  useLazyGetScheduleBaselineQuery,
  useCreateScheduleBaselineMutation,
  useDeleteScheduleBaselineMutation,

  // Settings hooks
  useGetScheduleSettingsQuery,
  useLazyGetScheduleSettingsQuery,
  useUpdateScheduleSettingsMutation,

  // Import/Export hooks
  useLazyGetImportTemplateQuery,
  useImportScheduleTasksMutation,
  useLazyExportScheduleTasksQuery,
  useLazyExportScheduleSummaryQuery,
  useLazyExportLookAheadQuery,
} = scheduleApi;

// Export endpoints for manual cache invalidation or prefetching
export const {
  getScheduleTasks,
  getScheduleTask,
  createScheduleTask,
  updateScheduleTask,
  deleteScheduleTask,
  updateScheduleTaskProgress,
  bulkReorderScheduleTasks,
  addScheduleDependency,
  removeScheduleDependency,
  getScheduleStats,
  getCriticalPath,
  getLookAhead,
  getScheduleBaselines,
  getScheduleBaseline,
  createScheduleBaseline,
  deleteScheduleBaseline,
  getScheduleSettings,
  updateScheduleSettings,
  getImportTemplate,
  importScheduleTasks,
  exportScheduleTasks,
  exportScheduleSummary,
  exportLookAhead,
} = scheduleApi.endpoints;

// Utility functions for cache management
export const scheduleApiUtils = {
  // Invalidate all schedule data for a project
  invalidateProjectSchedule: (dispatch: any, projectId: string) => {
    dispatch(
      scheduleApi.util.invalidateTags([
        { type: 'Schedule', id: `LIST_${projectId}` },
        { type: 'Schedule', id: `STATS_${projectId}` },
        { type: 'Schedule', id: `CRITICAL_PATH_${projectId}` },
        { type: 'Schedule', id: `LOOK_AHEAD_${projectId}` },
        { type: 'ScheduleBaseline', id: `LIST_${projectId}` },
        { type: 'ScheduleSettings', id: projectId },
      ])
    );
  },

  // Invalidate specific task
  invalidateTask: (dispatch: any, projectId: string, taskId: string) => {
    dispatch(
      scheduleApi.util.invalidateTags([
        { type: 'Schedule', id: taskId },
        { type: 'Schedule', id: `LIST_${projectId}` },
        { type: 'Schedule', id: `STATS_${projectId}` },
      ])
    );
  },

  // Prefetch schedule data
  prefetchSchedule: (dispatch: any, projectId: string) => {
    dispatch(getScheduleTasks.initiate({ projectId }));
    dispatch(getScheduleStats.initiate({ projectId }));
    dispatch(getScheduleSettings.initiate({ projectId }));
  },

  // Reset all schedule queries (useful on project change)
  resetScheduleState: (dispatch: any) => {
    dispatch(scheduleApi.util.resetApiState());
  },
};
